import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, ShieldCheck, FileText, Mail, ChevronRight, Loader2, User, X, Crown, Zap, CreditCard, BarChart3 } from "lucide-react";
import { NavBar } from "../components/NavBar";
import { useAuth } from "../lib/auth";
import { useEntitlements } from "../lib/entitlements";
import { createPortalSession } from "../lib/api";

export default function Settings() {
  const navigate = useNavigate();
  const { isAuthed, status: authStatus, user, signOut, deleteAccount } = useAuth();
  const { is_pro, comp, status, plan, trial_end, current_period_end, cancel_at_period_end } = useEntitlements();
  const [confirm, setConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { url } = await createPortalSession();
      window.location.href = url;
    } catch (e) {
      toast.error(e.message || "Could not open billing portal");
      setPortalBusy(false);
    }
  };

  useEffect(() => {
    if (authStatus === "loading") return;
    if (!isAuthed) navigate("/sign-in");
  }, [isAuthed, authStatus, navigate]);

  if (!user) return null;

  const memberSince = new Date(user.created_at).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const providerLabel = user.provider === "apple" ? "Apple" : "Google";
  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null);
  const trialEndLabel = fmtDate(trial_end);
  const periodEndLabel = fmtDate(current_period_end);

  const doSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await deleteAccount();
      toast.success("Account deleted");
      navigate("/");
    } catch (e) {
      toast.error(e.message || "Could not delete account");
    } finally {
      setDeleting(false);
      setConfirm(false);
    }
  };

  const Row = ({ icon: Icon, label, onClick, danger, testid }) => (
    <button
      onClick={onClick}
      data-testid={testid}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "16px 18px",
        background: "transparent",
        border: "none",
        cursor: "pointer",
        color: danger ? "var(--brand)" : "var(--on-surface)",
      }}
    >
      <span
        style={{
          width: 34,
          height: 34,
          borderRadius: "50%",
          background: danger ? "var(--brand-tertiary)" : "var(--surface-4)",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Icon size={16} color={danger ? "var(--brand)" : "var(--on-surface)"} />
      </span>
      <span style={{ flex: 1, textAlign: "left", fontWeight: 500, fontSize: 14.5 }}>{label}</span>
      <ChevronRight size={16} color="var(--muted)" />
    </button>
  );

  return (
    <>
      <NavBar />
      <main className="container-nc" style={{ paddingTop: 40, paddingBottom: 90, maxWidth: 620 }}>
        <span className="eyebrow">Account</span>
        <h1 style={{ fontSize: "clamp(2rem, 5vw, 2.8rem)", fontWeight: 600, letterSpacing: "-0.03em", margin: "8px 0 24px" }}>
          Settings
        </h1>

        {/* Account card */}
        <div className="card card-pad" data-testid="account-card" style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {user.picture ? (
            <img src={user.picture} alt="" style={{ width: 56, height: 56, borderRadius: "50%" }} />
          ) : (
            <span style={{ width: 56, height: 56, borderRadius: "50%", background: "var(--brand-tertiary)", display: "grid", placeItems: "center" }}>
              <User size={26} color="var(--brand)" />
            </span>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 17 }}>{user.name || user.email || "Signed in"}</div>
            {user.email && <div style={{ color: "var(--muted)", fontSize: 13, marginTop: 2 }}>{user.email}</div>}
            <div style={{ display: "flex", gap: 10, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
              <span className="chip chip-neutral">{providerLabel}</span>
              <span className="mono" style={{ color: "var(--muted)", fontSize: 11 }}>Member since {memberSince}</span>
            </div>
          </div>
        </div>

        <div className="eyebrow" style={{ margin: "28px 0 10px" }}>Subscription</div>
        <div className="card card-pad" data-testid="subscription-card" style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <span style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--brand-tertiary)", display: "grid", placeItems: "center" }}>
            {is_pro ? <Crown size={18} color="var(--brand-2)" /> : <Zap size={18} color="var(--brand)" />}
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>
              {!is_pro ? "No active subscription" : comp ? "Complimentary Pro" : status === "trialing" ? "Free trial" : plan === "pro_annual" ? "NarcCheck Pro — Yearly" : plan === "pro_monthly" ? "NarcCheck Pro — Monthly" : "NarcCheck Pro"}
            </div>
            <div style={{ color: "var(--muted)", fontSize: 13, marginTop: 2 }}>
              {!is_pro
                ? "Subscribe to unlock unlimited analyses"
                : comp
                ? "Full access granted by NarcCheck AI — no charge"
                : status === "trialing"
                ? `Trial${trialEndLabel ? ` — ${cancel_at_period_end ? "ends" : "first charge"} ${trialEndLabel}` : ""}`
                : cancel_at_period_end
                ? `Cancels${periodEndLabel ? ` on ${periodEndLabel}` : " at period end"} — access continues until then`
                : `Active${periodEndLabel ? ` — renews ${periodEndLabel}` : " · unlimited analyses"}`}
            </div>
          </div>
          {!is_pro && (
            <button className="btn btn-primary" onClick={() => navigate("/paywall")} data-testid="upgrade-btn">
              Subscribe
            </button>
          )}
          {is_pro && !comp && (
            <button className="btn btn-ghost" onClick={openPortal} disabled={portalBusy} data-testid="manage-subscription-btn">
              {portalBusy ? <Loader2 size={16} className="spin" /> : <><CreditCard size={16} /> Manage</>}
            </button>
          )}
        </div>
        {is_pro && !comp && (
          <p style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.6, marginTop: 10 }}>
            {status === "trialing"
              ? "Manage opens your secure Stripe billing portal. Cancel before your trial ends to avoid the $60 charge."
              : "Manage opens your secure Stripe billing portal where you can update your payment method, view invoices, or cancel — access continues until the end of your paid period."}
          </p>
        )}

        <div className="eyebrow" style={{ margin: "28px 0 10px" }}>Account actions</div>
        <div className="card">
          {user.role === "admin" && (
            <>
              <Row icon={BarChart3} label="Admin dashboard" onClick={() => navigate("/admin")} testid="admin-dashboard-link" />
              <div className="divider" style={{ margin: "0 18px" }} />
            </>
          )}
          <Row icon={LogOut} label="Sign out" onClick={doSignOut} testid="sign-out" />
          <div className="divider" style={{ margin: "0 18px" }} />
          <Row icon={Trash2} label="Delete account" onClick={() => setConfirm(true)} danger testid="delete-account-btn" />
        </div>
        <p style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.6, marginTop: 10 }}>
          Deleting your account permanently removes your saved history and signs you out on every device, including the iOS app.
        </p>

        <div className="eyebrow" style={{ margin: "28px 0 10px" }}>Legal</div>
        <div className="card">
          <Row icon={ShieldCheck} label="Privacy Policy" onClick={() => navigate("/privacy")} testid="privacy-link" />
          <div className="divider" style={{ margin: "0 18px" }} />
          <Row icon={FileText} label="Terms of Service" onClick={() => navigate("/terms")} testid="terms-link" />
          <div className="divider" style={{ margin: "0 18px" }} />
          <Row icon={Mail} label="Help & Support" onClick={() => navigate("/support")} testid="support-link" />
        </div>
        <p style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.6, marginTop: 10 }}>
          NarcCheck AI is for personal reflection and entertainment only. It is not a substitute for medical or psychological advice, diagnosis, or treatment. If you are in crisis, please reach out to a licensed professional.
        </p>
      </main>

      {confirm && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 90, background: "rgba(0,0,0,0.75)", display: "grid", placeItems: "center", padding: 20 }}
          onClick={() => !deleting && setConfirm(false)}
        >
          <div
            className="card card-pad fade-up"
            data-testid="delete-confirm-sheet"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 420, width: "100%", textAlign: "center" }}
          >
            <button className="btn-icon" style={{ marginLeft: "auto", display: "flex", background: "transparent", border: "none" }} onClick={() => setConfirm(false)}>
              <X size={18} color="var(--muted)" />
            </button>
            <span style={{ width: 56, height: 56, borderRadius: "50%", background: "var(--brand-tertiary)", display: "grid", placeItems: "center", margin: "0 auto 14px" }}>
              <Trash2 size={24} color="var(--brand)" />
            </span>
            <h3 style={{ margin: "0 0 8px", fontWeight: 600, fontSize: 19 }}>Delete your account?</h3>
            <p style={{ color: "var(--muted)", fontSize: 13.5, lineHeight: 1.6, margin: "0 0 20px" }}>
              This permanently removes your saved scorecards and signs you out everywhere. This cannot be undone.
            </p>
            <div style={{ display: "flex", gap: 12 }}>
              <button className="btn btn-ghost btn-block" onClick={() => setConfirm(false)} disabled={deleting} data-testid="delete-cancel">
                Cancel
              </button>
              <button className="btn btn-primary btn-block" onClick={doDelete} disabled={deleting} data-testid="delete-confirm">
                {deleting ? <Loader2 size={18} className="spin" /> : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
